import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLiveRefresh } from "../hooks/useLiveRefresh";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { fmtNum } from "../lib/format";
import { useAuth, hasModuleAccess, hasRole } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import ExportButtons from "../components/ExportButtons";
import AssetStore from "./AssetStore";
import { useSearchParams } from "react-router-dom";
import { roomTypeFor, linenForType, matchItem } from "../lib/roomLinen";

export default function Store() {
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tab, setTab] = useState("view");
  const canSeeAssets = hasModuleAccess(user, "asset_management", "view");
  const [outerTab, setOuterTab] = useState(
    canSeeAssets && sp.get("tab") === "assets" ? "assets" : "linen"
  );

  // Store issue to room (template-driven checklist)
  const uid = () => Math.random().toString(36).slice(2);
  const [roomId, setRoomId] = useState("");
  const [roomType, setRoomType] = useState(null);
  const [templateOk, setTemplateOk] = useState(true);
  const [issueRows, setIssueRows] = useState([]); // {item_id,name,par,current,required,storeAvail,checked,qty,remark}
  const [issueSearch, setIssueSearch] = useState("");
  const [remarks, setRemarks] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reqId, setReqId] = useState("");
  const [issueBusy, setIssueBusy] = useState(false);
  // Move Returned Awaiting → Store Clean (searchable checklist)
  const [retRows, setRetRows] = useState([]); // {item_id,name,size,available,checked,qty}
  const [retSearch, setRetSearch] = useState("");
  const [retBusy, setRetBusy] = useState(false);
  const canStoreManage = hasModuleAccess(user, "store_inventory", "manage");
  const canStockEdit = canStoreManage && hasModuleAccess(user, "master_linen_stock", "manage")
    && hasRole(user, "admin", "accounts", "hk_supervisor");
  const [physicalCount, setPhysicalCount] = useState({
    item_id: sp.get("adjustItem") || "",
    physical_qty: "",
    reason: "",
  });
  const [openingCount, setOpeningCount] = useState({ item_id: "", opening_qty: "", reason: "" });

  const load = useCallback(async () => {
    const [m, i, r] = await Promise.all([api.get("/store-inventory"), api.get("/items"), api.get("/rooms")]);
    setRows(m.data.rows);
    setItems(i.data);
    setRooms(r.data);
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  useLiveRefresh(load, outerTab === "linen" && tab === "view");

  // Build the Returned-Awaiting checklist from master-stock (only positive balances).
  useEffect(() => {
    setRetRows(rows.filter(r => Number(r.RETURNED_AWAITING) > 0).map(r => ({
      item_id: r.item_id, name: r.name, size: r.size,
      available: Number(r.RETURNED_AWAITING), checked: false, qty: "",
    })));
  }, [rows]);
  const updRet = (id, k, v) => setRetRows(x => x.map(r => (r.item_id === id ? { ...r, [k]: v } : r)));
  const visibleRet = useMemo(() => retRows.filter(r => r.name.toLowerCase().includes(retSearch.toLowerCase())), [retRows, retSearch]);
  const retRowValid = (r) => r.checked && /^\d+$/.test(String(r.qty)) && Number(r.qty) > 0 && Number(r.qty) <= r.available;
  const retCheckedInvalid = retRows.some(r => r.checked && !retRowValid(r));
  const retValidCount = retRows.filter(retRowValid).length;
  const setAllRet = (checked) => {
    const ids = new Set(visibleRet.map(r => r.item_id));
    setRetRows(x => x.map(r => (ids.has(r.item_id) ? { ...r, checked } : r)));
  };
  const physicalRow = rows.find(r => r.item_id === physicalCount.item_id);
  const openingRow = rows.find(r => r.item_id === openingCount.item_id);
  const physicalDifference = physicalRow && physicalCount.physical_qty !== ""
    ? Number(physicalCount.physical_qty) - Number(physicalRow.STORE_CLEAN || 0)
    : null;
  const openingDifference = openingRow && openingCount.opening_qty !== ""
    ? Number(openingCount.opening_qty) - Number(openingRow.opening_qty || 0)
    : null;

  const submitPhysicalCount = async () => {
    if (!physicalCount.item_id || physicalCount.physical_qty === "" || !physicalCount.reason.trim()) {
      return toast.error("Item, physical quantity, and reason are required");
    }
    try {
      const { data } = await api.post("/stock/physical-count", {
        item_id: physicalCount.item_id,
        location: "STORE_CLEAN",
        physical_qty: Number(physicalCount.physical_qty),
        reason: physicalCount.reason.trim(),
      });
      toast.success(`Physical count recorded (${data.difference >= 0 ? "+" : ""}${data.difference})`);
      setPhysicalCount({ item_id: "", physical_qty: "", reason: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitOpeningCorrection = async () => {
    if (!openingCount.item_id || openingCount.opening_qty === "" || !openingCount.reason.trim()) {
      return toast.error("Item, corrected opening quantity, and reason are required");
    }
    try {
      const { data } = await api.post("/stock/opening-corrections", {
        item_id: openingCount.item_id,
        location: "STORE_CLEAN",
        opening_qty: Number(openingCount.opening_qty),
        reason: openingCount.reason.trim(),
      });
      toast.success(`Opening correction recorded (${data.difference >= 0 ? "+" : ""}${data.difference})`);
      setOpeningCount({ item_id: "", opening_qty: "", reason: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Build the room-template linen list when a room is selected.
  useEffect(() => {
    if (!roomId || !items.length) { setIssueRows([]); setTemplateOk(true); return; }
    const room = rooms.find(r => r.id === roomId);
    const rtName = room?.room_type || roomTypeFor(room?.number);
    const labels = rtName ? linenForType(rtName) : [];
    if (!labels.length) { setIssueRows([]); setRoomType(null); setTemplateOk(false); return; }
    setRoomType(rtName); setTemplateOk(true);
    Promise.all([api.get(`/rooms/${roomId}/linen-balance`), api.get("/room-types")]).then(async ([balRes, rtRes]) => {
      const bal = balRes.data || {};
      const rt = (rtRes.data || []).find(x => (x.name || "").toLowerCase() === rtName.toLowerCase());
      let pars = {};
      if (rt) { try { const p = await api.get(`/room-types/${rt.id}/par`); (p.data || []).forEach(e => { pars[e.item_id] = e.par_qty; }); } catch (e) { console.debug("No par configured for room type", rt.id, e); } }
      const built = labels.map(label => {
        const it = matchItem(label, items);
        if (!it || it.launderable === false) return null; // exclude Extra Bed / unmatched
        const par = pars[it.id];
        const current = bal[it.id] || 0;
        const storeRow = rows.find(r => r.item_id === it.id);
        return {
          item_id: it.id, label, name: it.name,
          par: par == null ? null : par, current,
          required: par == null ? null : Math.max(0, par - current),
          storeAvail: storeRow ? storeRow.STORE_CLEAN : 0,
          checked: false, qty: "", remark: "",
        };
      }).filter(Boolean);
      setIssueRows(built);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, items, rooms]);

  const updIssue = (id, k, v) => setIssueRows(rowsX => rowsX.map(r => (r.item_id === id ? { ...r, [k]: v } : r)));
  const visibleIssue = useMemo(() => issueRows.filter(r => r.name.toLowerCase().includes(issueSearch.toLowerCase())), [issueRows, issueSearch]);
  const issueRowValid = (r) => r.checked && /^\d+$/.test(String(r.qty)) && Number(r.qty) > 0 && Number(r.qty) <= r.storeAvail;
  const issueCheckedInvalid = issueRows.some(r => r.checked && !issueRowValid(r));
  const issueValidCount = issueRows.filter(issueRowValid).length;
  const setAllIssue = (checked) => {
    const ids = new Set(visibleIssue.map(r => r.item_id));
    setIssueRows(rowsX => rowsX.map(r => (ids.has(r.item_id) ? { ...r, checked } : r)));
  };

  const openConfirm = () => {
    if (issueCheckedInvalid) { toast.error("Fix issue quantities (whole numbers not exceeding Store Clean)"); return; }
    if (issueValidCount === 0) { toast.error("Tick at least one item with a valid quantity"); return; }
    setReqId(uid() + uid()); setConfirmOpen(true);
  };
  const submitIssue = async () => {
    const clean = issueRows.filter(issueRowValid);
    const rowRemarks = clean.filter(r => r.remark).map(r => `${r.name}: ${r.remark}`).join("; ");
    setIssueBusy(true);
    try {
      const { data } = await api.post("/room-issue", {
        room_id: roomId, client_request_id: reqId,
        remarks: [remarks, rowRemarks].filter(Boolean).join(" | "),
        lines: clean.map(r => ({ item_id: r.item_id, qty: Number(r.qty) })),
      });
      toast.success(data.duplicate ? "Already issued" : "Linen issued to room");
      setConfirmOpen(false); setRoomId(""); setIssueRows([]); setRemarks(""); setIssueSearch(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setIssueBusy(false); }
  };

  const submitReturn = async () => {
    if (retCheckedInvalid) { toast.error("Fix quantities (whole numbers not exceeding Returned Awaiting)"); return; }
    const clean = retRows.filter(retRowValid);
    if (!clean.length) { toast.error("Tick at least one item with a valid quantity"); return; }
    setRetBusy(true);
    try {
      const { data } = await api.post("/laundry/store-return", {
        target: "STORE_CLEAN", client_request_id: uid() + uid(),
        lines: clean.map(r => ({ item_id: r.item_id, qty: Number(r.qty) })),
      });
      toast.success(data.duplicate ? "Already moved" : "Returned to Store Clean");
      setRetSearch(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRetBusy(false); }
  };

  return (
    <Layout>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="section-title" data-testid="page-title">{t("nav.store")}</h1>
          <div className="section-sub">{outerTab === "assets" ? "Bulk reusable and consumable asset stock across store and rooms." : t("sub.store")}</div>
        </div>
        {outerTab === "linen" && <ExportButtons endpoint="/reports/export/store-inventory" filenameBase="store-inventory" />}
      </div>

      <Tabs value={outerTab} onValueChange={setOuterTab} className="mb-4">
        <TabsList data-testid="store-outer-tabs">
          <TabsTrigger value="linen" data-testid="store-tab-linen">Linen Inventory</TabsTrigger>
          {canSeeAssets && (
            <TabsTrigger value="assets" data-testid="store-tab-assets">
              Assets &amp; Consumables
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="linen">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="view" data-testid="tab-store-view">Current Balance</TabsTrigger>
          {canStoreManage && <TabsTrigger value="issue" data-testid="tab-store-issue">Issue to Room</TabsTrigger>}
          {canStoreManage && <TabsTrigger value="return" data-testid="tab-store-return">Move Returned → Store</TabsTrigger>}
        </TabsList>

        <TabsContent value="view">
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="vr-table">
                <thead><tr><th>Item</th><th className="num">Store Clean</th><th className="num">Returned Awaiting</th><th className="num">Available</th><th className="num">Threshold</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.item_id}>
                      <td>{r.name}</td>
                      <td className="num">{fmtNum(r.STORE_CLEAN)}</td>
                      <td className="num">{fmtNum(r.RETURNED_AWAITING)}</td>
                      <td className="num font-semibold">{fmtNum(r.available_stock)}</td>
                      <td className="num text-slate-500">{r.low_stock_threshold}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {canStockEdit && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
              <Card className="p-4" data-testid="linen-physical-count-card">
                <div className="section-sub mb-3 font-medium text-slate-700">Set Physical Count</div>
                <div className="text-xs text-slate-500 mb-3" data-testid="linen-physical-location">Stock location: Store Clean</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div>
                    <Label>Item</Label>
                    <Select
                      value={physicalCount.item_id}
                      onValueChange={item_id => setPhysicalCount({ ...physicalCount, item_id })}
                    >
                      <SelectTrigger className="h-11 mt-1" data-testid="linen-physical-item">
                        <SelectValue placeholder="Select item" />
                      </SelectTrigger>
                      <SelectContent>
                        {rows.map(r => <SelectItem key={r.item_id} value={r.item_id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>System Qty (Store Clean)</Label>
                    <div className="h-11 mt-1 px-3 flex items-center border rounded-md bg-slate-50" data-testid="linen-physical-system-qty">
                      {physicalRow ? fmtNum(physicalRow.STORE_CLEAN || 0) : "—"}
                    </div>
                  </div>
                  <div>
                    <Label>Physical Qty</Label>
                    <Input type="number" min="0" value={physicalCount.physical_qty} onChange={e => setPhysicalCount({ ...physicalCount, physical_qty: e.target.value })} className="h-11 mt-1" data-testid="linen-physical-qty" />
                    <div className="text-[11px] text-slate-500 mt-1" data-testid="linen-physical-difference">Difference: {physicalDifference == null ? "—" : `${physicalDifference >= 0 ? "+" : ""}${fmtNum(physicalDifference)}`}</div>
                  </div>
                  <div>
                    <Label>Reason *</Label>
                    <Input value={physicalCount.reason} onChange={e => setPhysicalCount({ ...physicalCount, reason: e.target.value })} className="h-11 mt-1" data-testid="linen-physical-reason" />
                  </div>
                </div>
                <Button onClick={submitPhysicalCount} className="mt-3 bg-slate-900 text-white hover:bg-slate-800" data-testid="linen-physical-submit">Post Adjustment</Button>
              </Card>
              <Card className="p-4" data-testid="linen-opening-correction-card">
                <div className="section-sub mb-3 font-medium text-slate-700">Correct Opening Stock</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div>
                    <Label>Item</Label>
                    <Select value={openingCount.item_id} onValueChange={item_id => setOpeningCount({ ...openingCount, item_id })}>
                      <SelectTrigger className="h-11 mt-1" data-testid="linen-opening-item"><SelectValue placeholder="Select item" /></SelectTrigger>
                      <SelectContent>{rows.map(r => <SelectItem key={r.item_id} value={r.item_id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Existing Opening</Label>
                    <div className="h-11 mt-1 px-3 flex items-center border rounded-md bg-slate-50" data-testid="linen-opening-system-qty">{openingRow ? fmtNum(openingRow.opening_qty || 0) : "—"}</div>
                  </div>
                  <div>
                    <Label>Corrected Opening</Label>
                    <Input type="number" min="0" value={openingCount.opening_qty} onChange={e => setOpeningCount({ ...openingCount, opening_qty: e.target.value })} className="h-11 mt-1" data-testid="linen-opening-qty" />
                    <div className="text-[11px] text-slate-500 mt-1" data-testid="linen-opening-difference">Difference: {openingDifference == null ? "—" : `${openingDifference >= 0 ? "+" : ""}${fmtNum(openingDifference)}`}</div>
                  </div>
                  <div>
                    <Label>Reason *</Label>
                    <Input value={openingCount.reason} onChange={e => setOpeningCount({ ...openingCount, reason: e.target.value })} className="h-11 mt-1" data-testid="linen-opening-reason" />
                  </div>
                </div>
                <Button onClick={submitOpeningCorrection} className="mt-3 bg-slate-900 text-white hover:bg-slate-800" data-testid="linen-opening-submit">Post Opening Correction</Button>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="issue">
          <Card className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div><Label>Room</Label>
                <Select value={roomId} onValueChange={setRoomId}><SelectTrigger className="h-12 mt-1" data-testid="issue-room"><SelectValue placeholder="Pick room" /></SelectTrigger>
                  <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.number}{r.room_type ? ` — ${r.room_type}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Remarks</Label><Input value={remarks} onChange={e => setRemarks(e.target.value)} className="h-12 mt-1" data-testid="issue-remarks" /></div>
            </div>

            {!roomId ? (
              <div className="p-3 rounded border bg-slate-50 text-sm text-slate-600" data-testid="issue-pick-room">Select a room to load its linen template.</div>
            ) : !templateOk ? (
              <div className="p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-800" data-testid="issue-not-configured">Room linen template is not configured. Complete the room mapping before issuing linen.</div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="text-sm font-semibold text-slate-700">{roomType} template</div>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Search linen…" value={issueSearch} onChange={e => setIssueSearch(e.target.value)} className="h-9 w-44" data-testid="issue-search" />
                    <Button type="button" variant="outline" size="sm" onClick={() => setAllIssue(true)} data-testid="issue-select-all">Select All</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setAllIssue(false)} data-testid="issue-clear-all">Clear All</Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="vr-table" data-testid="issue-checklist">
                    <thead><tr>
                      <th className="w-8"></th><th>Linen Item</th><th className="num">Par</th><th className="num">In Room</th>
                      <th className="num">Required</th><th className="num">Store Clean</th><th className="num">Issue Qty</th><th>Remarks</th>
                    </tr></thead>
                    <tbody>
                      {visibleIssue.map((r, i) => (
                        <tr key={r.item_id}>
                          <td><input type="checkbox" checked={r.checked} disabled={r.storeAvail <= 0} onChange={e => updIssue(r.item_id, "checked", e.target.checked)} data-testid={`issue-check-${i}`} /></td>
                          <td>{r.label}{r.storeAvail <= 0 ? <span className="text-xs text-amber-600 ml-1">· out of stock</span> : ""}</td>
                          <td className="num">{r.par == null ? <span className="text-slate-400 text-xs">Not Configured</span> : fmtNum(r.par)}</td>
                          <td className="num">{fmtNum(r.current)}</td>
                          <td className="num">{r.required == null ? <span className="text-slate-400 text-xs">Not Configured</span> : fmtNum(r.required)}</td>
                          <td className="num">{fmtNum(r.storeAvail)}</td>
                          <td className="num"><Input type="number" min="1" step="1" placeholder="" value={r.qty} disabled={!r.checked} onChange={e => updIssue(r.item_id, "qty", e.target.value)} className={`h-9 w-24 ml-auto text-right ${r.checked && !issueRowValid(r) ? "border-red-400" : ""}`} data-testid={`issue-qty-${i}`} /></td>
                          <td><Input value={r.remark} disabled={!r.checked} onChange={e => updIssue(r.item_id, "remark", e.target.value)} className="h-9" data-testid={`issue-remark-${i}`} /></td>
                        </tr>
                      ))}
                      {visibleIssue.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-4">No linen items for this template{issueSearch ? " match your search" : ""}.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-slate-400 mt-1">Only ticked rows are issued. Issue Quantity cannot exceed Store Clean availability.</div>
                <div className="mt-4"><Button className="bg-slate-900 text-white hover:bg-slate-800 h-12 px-8" onClick={openConfirm} disabled={issueValidCount === 0 || issueCheckedInvalid} data-testid="issue-submit">Issue to Room</Button></div>
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="return">
          <Card className="p-4 md:p-6">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="text-sm text-slate-600">Move accepted returned linen from Returned Awaiting Storage into Store Clean. Only items currently awaiting storage are listed.</div>
              <div className="flex items-center gap-2">
                <Input placeholder="Search linen…" value={retSearch} onChange={e => setRetSearch(e.target.value)} className="h-9 w-44" data-testid="return-search" />
                <Button type="button" variant="outline" size="sm" onClick={() => setAllRet(true)} data-testid="return-select-all">Select All</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setAllRet(false)} data-testid="return-clear-all">Clear All</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="vr-table" data-testid="return-checklist">
                <thead><tr><th className="w-8"></th><th>Linen Item</th><th className="num">Returned Awaiting</th><th className="num">Move Qty</th></tr></thead>
                <tbody>
                  {visibleRet.map((r, i) => (
                    <tr key={r.item_id}>
                      <td><input type="checkbox" checked={r.checked} onChange={e => updRet(r.item_id, "checked", e.target.checked)} data-testid={`return-check-${i}`} /></td>
                      <td>{r.name}</td>
                      <td className="num">{fmtNum(r.available)}</td>
                      <td className="num"><Input type="number" min="1" step="1" max={r.available} placeholder="" value={r.qty} disabled={!r.checked} onChange={e => updRet(r.item_id, "qty", e.target.value)} className={`h-9 w-24 ml-auto text-right ${r.checked && !retRowValid(r) ? "border-red-400" : ""}`} data-testid={`return-qty-${i}`} /></td>
                    </tr>
                  ))}
                  {visibleRet.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-4">{retRows.length === 0 ? "Nothing awaiting storage right now." : "No items match your search."}</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-slate-400 mt-1">Only ticked rows are moved. Move Qty cannot exceed Returned Awaiting.</div>
            <div className="mt-4"><Button className="bg-slate-900 text-white hover:bg-slate-800 h-12 px-8" onClick={submitReturn} disabled={retValidCount === 0 || retCheckedInvalid || retBusy} data-testid="return-submit">Move to Store Clean</Button></div>
          </Card>
        </TabsContent>
      </Tabs>
        </TabsContent>
        {canSeeAssets && (
          <TabsContent value="assets">
            <AssetStore embedded />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Issue to {rooms.find(r => r.id === roomId)?.number ? `Room ${rooms.find(r => r.id === roomId)?.number}` : "room"}{roomType ? ` — ${roomType}` : ""}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 mb-2">These quantities will move from Store Clean into the room (once):</div>
          <table className="vr-table"><thead><tr><th>Linen Item</th><th className="num">Issue Qty</th></tr></thead>
            <tbody>{issueRows.filter(issueRowValid).map(r => <tr key={r.item_id}><td>{r.name}</td><td className="num">{fmtNum(Number(r.qty))}</td></tr>)}</tbody>
          </table>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={submitIssue} disabled={issueBusy} data-testid="issue-confirm">Confirm Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
